import React, { ChangeEvent, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import Page from "components/page/Page";
import { StatusCode } from "api/protocols";
import Input from "components/form/input/Input";
import FormLayout from "components/form/FormLayout";
import Card from "components/card/Card";
import Form from "components/form/Form";
import { isEqualWith, isNull } from "lodash";
import { IAuthState } from "redux/reducers/auth";
import { IAuthActions } from "redux/actions/auth";
import { IUIActions } from "redux/actions/ui";
import { IFacilityState } from "redux/reducers/facility";
import { isPositiveInteger } from "helpers/Helpers";
import Popup from "components/popup/Popup";
import { IUserPermissions } from "redux/reducers/models/user";

import { useTranslation } from "react-i18next";
import { Badge } from "components/badge/Badge";
import Icon from "components/icon/Icon";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import {
  GetFacilityAdmin,
  PostPasswordResetCode,
  PutDisableFacilityAdmin,
  PutFacilityAdmin,
  PutReactivateFacilityAdmin,
  PutFacilityAdminPermissions,
} from "api/rpc/2024-04/facilityAdmin/client/admin/admin";
import Checkbox from "components/form/checkbox/Checkbox";

interface IFacilityStaffEditProps {
  authStore: IAuthState;
  authActions: IAuthActions;
  uiActions: IUIActions;
  facilityStore: IFacilityState;
}

interface IStaff {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  username: string;
  state: string;
  quick_code: string;
  permissions: IUserPermissions;
}

export default function FacilityStaffEdit(props: IFacilityStaffEditProps) {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const history = useHistory();

  const permissions = props?.authStore?.user?.permissions;
  const facilityPermissions = props?.facilityStore?.facility?.permissions;

  const [staffState, setStaffState] = useState<IStaff>(undefined);
  const [staffStateBeforeChanges, setStaffStateBeforeChanges] = useState<IStaff>(undefined);

  const [resetPasswordPopup, setResetPasswordPopup] = useState<boolean>(false);
  const [reactivateAccountPopup, setReactivateAccountPopup] = useState<boolean>(false);

  useEffect(() => {
    void loadFacilityStaff();
  }, []);

  async function loadFacilityStaff() {
    const params = {
      id: id,
      extended: true,
    };
    const staffRes = await GetFacilityAdmin(params, true);
    if (staffRes.status !== StatusCode.OK) {
      props.uiActions.showError(t("Error when loading staff member"));
      return;
    }

    const permissions: IUserPermissions & { [key: string]: any } = { ...staffRes.data[0].permissions };

    for (const item of Object.keys(permissions)) {
      permissions[item] = !!permissions[item];
    }

    setStaffState(prev => ({
      ...prev,
      id: staffRes.data[0].id,
      first_name: staffRes.data[0].first_name ?? "",
      last_name: staffRes.data[0].last_name ?? "",
      email: staffRes.data[0].email ?? "",
      username: staffRes.data[0].username ?? "",
      state: staffRes.data[0].state ?? "",
      quick_code: "",
      permissions: permissions,
    }));
  }

  async function saveStaffDetails() {
    const isEmpty = (x: any) => x === "" || x === null || x === undefined;

    if (!isEmpty(staffState.quick_code) && !isPositiveInteger(staffState.quick_code)) {
      props.uiActions.showError(t("Quick code must be a positive integer"));
      return;
    }

    const putFacilityAdminResponse = await PutFacilityAdmin(
      {
        id: staffState.id,
        first_name: staffState.first_name,
        last_name: staffState.last_name,
        email: staffState.email,
        username: staffState.username,
        quick_code: isEmpty(staffState.quick_code) ? undefined : parseInt(staffState.quick_code),
      },
      true,
    );

    if (putFacilityAdminResponse.status !== StatusCode.OK) {
      props.uiActions.showError(t("Error when updating staff member"));
      return;
    }

    const putFacilityAdminPermissionsResponse = await PutFacilityAdminPermissions(
      {
        user_id: parseInt(id),
        ...staffState.permissions,
      },
      true,
    );

    if (putFacilityAdminPermissionsResponse.status !== StatusCode.OK) {
      props.uiActions.showError(t("Error when updating staff member permissions"));
      return;
    }

    props.uiActions.showSuccess(t("Staff account saved successfully"));

    setStaffStateBeforeChanges(staffState);
  }

  async function disableStaffAccount() {
    const putDisableFacilityAdminResponse = await PutDisableFacilityAdmin({ admin_id: staffState.id }, true);

    if (putDisableFacilityAdminResponse.status !== StatusCode.OK) {
      props.uiActions.showError(t("Failed to disable user"));
      return;
    }

    history.push("/admin/settings/staff");
  }

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setStaffState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleToggleChange(event: ChangeEvent<HTMLInputElement>) {
    const id = event?.target?.id;
    const checked = event?.target?.checked;

    if (id === undefined || checked === undefined) {
      return;
    }

    setStaffState(prev => {
      return { ...prev, permissions: { ...prev.permissions, [id]: checked } };
    });
  }

  function handleProductsEditToggleChange(event: ChangeEvent<HTMLInputElement>) {
    const checked = event?.target?.checked;

    if (checked === undefined) {
      return;
    }

    setStaffState(prev => {
      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          products_edit: checked,
          products_edit_cost: false,
          products_edit_price: false,
        },
      };
    });
  }

  function unsavedChangesExist() {
    if (staffStateBeforeChanges === undefined) {
      if (staffState) {
        setStaffStateBeforeChanges(staffState);
      }
      return false;
    }

    return !isEqualWith(staffStateBeforeChanges, staffState, (originalValue, newValue) => {
      if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
        return true;
      }
    });
  }

  function cancelUnsavedChanges() {
    setStaffState(staffStateBeforeChanges);
  }

  const disableAction = {
    content: t("Disable"),
    action: disableStaffAccount,
  };

  const disablePermissions =
    permissions &&
    staffState &&
    staffState.state !== "disabled" &&
    permissions.user_id !== staffState.id &&
    permissions.admin_accounts_disable;

  const secondaryActions = [
    {
      content: t("Reset Password"),
      action: () => setResetPasswordPopup(true),
    },
    ...(disablePermissions ? [disableAction] : []),
  ];

  async function handlePasswordReset() {
    const resetRes = await PostPasswordResetCode(
      { email: staffStateBeforeChanges.email, facility_id: props.authStore?.user?.facility_id ?? undefined },
      true,
    );
    if (resetRes.status !== StatusCode.OK) {
      props.uiActions.showError(t("Error sending email"));
      return;
    }
    setResetPasswordPopup(false);
    props.uiActions.showSuccess(t("Email sent successfully"));
  }

  const primaryAction = {
    content: t("Reactivate Account"),
    action: () => setReactivateAccountPopup(true),
  };

  async function handleReactivateAccount() {
    const activateRes = await PutReactivateFacilityAdmin({ admin_id: staffState.id }, true);

    if (activateRes.status !== StatusCode.OK) {
      props.uiActions.showError(t("Disabled"));
    }

    history.push("/admin/settings/staff");
  }

  return (
    <Page
      title={`${t("Edit Staff Member")} ${id}`}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Staff Accounts"),
          url: "/admin/settings/staff",
        },
      ]}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: () => void saveStaffDetails(),
        onCancel: cancelUnsavedChanges,
      }}
      primaryAction={staffState?.state === "disabled" ? primaryAction : null}
      secondaryActions={secondaryActions}
    >
      <div className="mb-4">
        {staffState?.state === "disabled" ? (
          <Badge type="error" size="medium" iconLeft={<Icon icon={faTimes} size="xsmall" />} iconSize="small">
            {t("Disabled")}
          </Badge>
        ) : null}
      </div>

      <div className="mb-10">
        {staffState && staffStateBeforeChanges && (
          <Form>
            <Card>
              <Card.Section>
                <FormLayout>
                  <FormLayout.Group>
                    <Input
                      value={staffState.first_name}
                      label={t("First Name")}
                      id="first_name"
                      onChange={handleInputChange}
                      placeholder={t("First Name")}
                      disabled={
                        staffState.state === "disabled" ||
                        (permissions?.user_id !== staffState.id && !permissions?.admin_accounts_edit)
                      }
                    />
                    <Input
                      value={staffState.last_name}
                      label={t("Last Name")}
                      id="last_name"
                      onChange={handleInputChange}
                      placeholder={t("Last Name")}
                      disabled={
                        staffState.state === "disabled" ||
                        (permissions?.user_id !== staffState.id && !permissions?.admin_accounts_edit)
                      }
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <Input
                      value={staffState.email}
                      label={t("Email")}
                      id="email"
                      onChange={handleInputChange}
                      placeholder={t("Email")}
                      disabled={
                        staffState.state === "disabled" ||
                        (permissions?.user_id !== staffState.id && !permissions?.admin_accounts_edit)
                      }
                    />
                    <Input
                      value={staffState.username}
                      label={t("Username")}
                      id="username"
                      onChange={handleInputChange}
                      placeholder={t("Username")}
                      disabled={
                        staffState.state === "disabled" ||
                        (permissions?.user_id !== staffState.id && !permissions?.admin_accounts_edit)
                      }
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    {staffState.state !== "disabled" &&
                      (permissions?.user_id === staffState.id || permissions?.admin_accounts_edit) && (
                        <Input
                          value={staffState.quick_code}
                          label={t("Quick Code")}
                          id="quick_code"
                          onChange={handleInputChange}
                          placeholder={t("Quick Code")}
                        />
                      )}
                  </FormLayout.Group>
                </FormLayout>
              </Card.Section>
            </Card>
            {staffState.state !== "disabled" &&
              permissions?.admin_accounts_edit &&
              permissions?.admin_permissions_edit && (
                <Card title={t("Permissions")}>
                  <Card.Section></Card.Section>
                  <Card.Section>
                    <div className="flex items-start">
                      <div className="w-1/2">
                        <h1>{t("Staff Accounts")}</h1>
                      </div>

                      <div className="w-full">
                        <div>
                          <Checkbox
                            id="admin_accounts_disable"
                            checked={staffState.permissions.admin_accounts_disable}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Disable Staff Accounts")}
                            disabled={permissions?.user_id === staffState.id}
                          />

                          <Checkbox
                            id="admin_accounts_edit"
                            checked={staffState.permissions.admin_accounts_edit}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Edit Staff Accounts")}
                            disabled={permissions?.user_id === staffState.id}
                          />

                          {staffState.permissions.admin_accounts_edit && (
                            <div className="ml-3">
                              <Checkbox
                                id="admin_permissions_edit"
                                checked={staffState.permissions.admin_permissions_edit}
                                onChange={handleToggleChange}
                                size="medium"
                                label={t("Account Permissions")}
                                disabled={permissions?.user_id === staffState.id}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card.Section>

                  <Card.Section>
                    <div className="flex items-start">
                      <div className="w-1/2">
                        <h1>{t("Products")}</h1>
                      </div>

                      <div className="w-full">
                        <div>
                          <Checkbox
                            id="products_archive"
                            checked={staffState.permissions.products_archive}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Archive Products")}
                            disabled={permissions?.user_id === staffState.id}
                          />

                          <Checkbox
                            id="products_create"
                            checked={staffState.permissions.products_create}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Create Products")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="products_create"
                            checked={staffState.permissions.products_edit}
                            onChange={handleProductsEditToggleChange}
                            size="medium"
                            label={t("Edit Products")}
                            disabled={permissions?.user_id === staffState.id}
                          />

                          {staffState.permissions.products_edit && (
                            <div className="ml-3">
                              <Checkbox
                                checked={staffState.permissions.products_edit_cost}
                                onChange={handleToggleChange}
                                id="products_edit_cost"
                                disabled={permissions?.user_id === staffState.id}
                                size="medium"
                                label={t("Edit Product Cost")}
                              />
                              <Checkbox
                                checked={staffState.permissions.products_edit_price}
                                id="products_edit_price"
                                onChange={handleToggleChange}
                                disabled={permissions?.user_id === staffState.id}
                                size="medium"
                                label={t("Edit Product Price")}
                              />
                            </div>
                          )}

                          <Checkbox
                            checked={staffState.permissions.discounts_create}
                            id="discounts_create"
                            onChange={handleToggleChange}
                            disabled={permissions?.user_id === staffState.id}
                            size="medium"
                            label={t("Create Discounts")}
                          />
                        </div>
                      </div>
                    </div>
                  </Card.Section>

                  <Card.Section>
                    <div className="flex items-start">
                      <div className="w-1/2">
                        <h1>{t("Tickets")}</h1>
                      </div>

                      <div className="w-full">
                        <div>
                          <Checkbox
                            id="tickets_archive"
                            checked={staffState.permissions.tickets_archive}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Archive Tickets")}
                            disabled={permissions?.user_id === staffState.id}
                          />

                          <Checkbox
                            id="tickets_unredeem"
                            checked={staffState.permissions.tickets_unredeem}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Unredeem ticket")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                        </div>
                      </div>
                    </div>
                  </Card.Section>

                  <Card.Section>
                    <div className="flex items-start">
                      <div className="w-1/2">
                        <h1>{t("Line Items")}</h1>
                      </div>

                      <div className="w-full">
                        <div>
                          <Checkbox
                            id="orders_edit_line_item_price"
                            checked={staffState.permissions.orders_edit_line_item_price}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Edit Line Item Price")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="orders_apply_discount"
                            checked={staffState.permissions.orders_apply_discount}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Apply Discounts")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="orders_remove_line_item"
                            checked={staffState.permissions.orders_remove_line_item}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Remove Line Item")}
                            disabled={permissions?.user_id === staffState.id}
                          />

                          {staffState.permissions.orders_remove_line_item && (
                            <div className="ml-3">
                              <Checkbox
                                id="orders_remove_line_item_after_send"
                                checked={staffState.permissions.orders_remove_line_item_after_send}
                                onChange={handleToggleChange}
                                size="medium"
                                label={t("Remove Line Item After Send")}
                                disabled={permissions?.user_id === staffState.id}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card.Section>

                  <Card.Section>
                    <div className="flex items-start">
                      <div className="w-1/2">
                        <h1>{t("Reports")}</h1>
                      </div>

                      <div className="w-full">
                        <div>
                          <Checkbox
                            id="reports_access"
                            checked={staffState.permissions.reports_access}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Reports Access")}
                            disabled={permissions?.user_id === staffState.id}
                          />

                          {staffState.permissions.reports_access && (
                            <div className="flex justify-between">
                              <div className="ml-3">
                                <Checkbox
                                  id="reports_facility_transactions"
                                  checked={staffState.permissions.reports_facility_transactions}
                                  onChange={handleToggleChange}
                                  size="medium"
                                  label={t("Transactions")}
                                  disabled={permissions?.user_id === staffState.id}
                                />
                                <Checkbox
                                  id="reports_facility_scheduled_payments"
                                  checked={staffState.permissions.reports_facility_scheduled_payments}
                                  onChange={handleToggleChange}
                                  size="medium"
                                  label={t("Scheduled Payments")}
                                  disabled={permissions?.user_id === staffState.id}
                                />
                                <Checkbox
                                  id="reports_facility_detailed_sales_by_department"
                                  checked={staffState.permissions.reports_facility_detailed_sales_by_department}
                                  onChange={handleToggleChange}
                                  size="medium"
                                  label={t("Detailed Sales By Department")}
                                  disabled={permissions?.user_id === staffState.id}
                                />
                                <Checkbox
                                  id="reports_facility_sales_by_accounting_reference"
                                  checked={staffState.permissions.reports_facility_sales_by_accounting_reference}
                                  onChange={handleToggleChange}
                                  size="medium"
                                  label={t("Sales By Accounting Reference")}
                                  disabled={permissions?.user_id === staffState.id}
                                />
                                <Checkbox
                                  id="reports_facility_inventory_listing_by_department"
                                  checked={staffState.permissions.reports_facility_inventory_listing_by_department}
                                  onChange={handleToggleChange}
                                  size="medium"
                                  label={t("Inventory Listing By Department")}
                                  disabled={permissions?.user_id === staffState.id}
                                />
                                <Checkbox
                                  id="reports_facility_order_exceptions"
                                  checked={staffState.permissions.reports_facility_order_exceptions}
                                  onChange={handleToggleChange}
                                  size="medium"
                                  label={t("Order Exceptions")}
                                  disabled={permissions?.user_id === staffState.id}
                                />
                                {/* <Checkbox
                              id="reports_facility_golfnorth_daily_sales"
                              checked={staffState.permissions.reports_facility_golfnorth_daily_sales}
                              onChange={handleToggleChange}
                              size="medium"
                              label={t("GolfNorth Daily Sales")}
                              disabled={permissions?.user_id === staffState.id}
                            /> */}
                              </div>

                              <div className="ml-3">
                                <Checkbox
                                  id="reports_facility_ticket_overview"
                                  checked={staffState.permissions.reports_facility_ticket_overview}
                                  onChange={handleToggleChange}
                                  size="medium"
                                  label={t("Ticket Overview")}
                                  disabled={permissions?.user_id === staffState.id}
                                />
                                <Checkbox
                                  id="reports_facility_aged_accounts_receivable"
                                  checked={staffState.permissions.reports_facility_aged_accounts_receivable}
                                  onChange={handleToggleChange}
                                  size="medium"
                                  label={t("Aged Accounts Receivable")}
                                  disabled={permissions?.user_id === staffState.id}
                                />
                                <Checkbox
                                  id="reports_facility_inventory_adjustments"
                                  checked={staffState.permissions.reports_facility_inventory_adjustments}
                                  onChange={handleToggleChange}
                                  size="medium"
                                  label={t("Inventory Adjustments")}
                                  disabled={permissions?.user_id === staffState.id}
                                />

                                <Checkbox
                                  id="reports_facility_gift_card_activity"
                                  checked={staffState.permissions.reports_facility_gift_card_activity}
                                  onChange={handleToggleChange}
                                  size="medium"
                                  label={t("Gift Card Activity")}
                                  disabled={permissions?.user_id === staffState.id}
                                />

                                <Checkbox
                                  id="reports_facility_tickets_redeemed"
                                  checked={staffState.permissions.reports_facility_tickets_redeemed}
                                  onChange={handleToggleChange}
                                  size="medium"
                                  label={t("Tickets Redeemed")}
                                  disabled={permissions?.user_id === staffState.id}
                                />

                                <Checkbox
                                  id="reports_facility_tickets"
                                  checked={staffState.permissions.reports_facility_tickets}
                                  onChange={handleToggleChange}
                                  size="medium"
                                  label={t("Tickets")}
                                  disabled={permissions?.user_id === staffState.id}
                                />

                                <Checkbox
                                  id="reports_facility_tickets_expired"
                                  checked={staffState.permissions.reports_facility_tickets_expired}
                                  onChange={handleToggleChange}
                                  size="medium"
                                  label={t("Tickets Expired")}
                                  disabled={permissions?.user_id === staffState.id}
                                />
                              </div>

                              <div className="ml-3">
                                <Checkbox
                                  id="reports_facility_rounds_by_customer_type"
                                  checked={staffState.permissions.reports_facility_rounds_by_customer_type}
                                  onChange={handleToggleChange}
                                  size="medium"
                                  label={t("Rounds By Customer Type")}
                                  disabled={permissions?.user_id === staffState.id}
                                />
                                <Checkbox
                                  id="reports_facility_membership_sales"
                                  checked={staffState.permissions.reports_facility_membership_sales}
                                  onChange={handleToggleChange}
                                  size="medium"
                                  label={t("Membership Sales")}
                                  disabled={permissions?.user_id === staffState.id}
                                />
                                <Checkbox
                                  id="reports_facility_player_spend"
                                  checked={staffState.permissions.reports_facility_player_spend}
                                  onChange={handleToggleChange}
                                  size="medium"
                                  label={t("Player Spend")}
                                  disabled={permissions?.user_id === staffState.id}
                                />
                                <Checkbox
                                  id="reports_facility_sales_by_department"
                                  checked={staffState.permissions.reports_facility_sales_by_department}
                                  onChange={handleToggleChange}
                                  size="medium"
                                  label={t("Sales By Department")}
                                  disabled={permissions?.user_id === staffState.id}
                                />
                                <Checkbox
                                  id="reports_facility_tee_sheet_utilization"
                                  checked={staffState.permissions.reports_facility_tee_sheet_utilization}
                                  onChange={handleToggleChange}
                                  size="medium"
                                  label={t("Tee Sheet Utilization")}
                                  disabled={permissions?.user_id === staffState.id}
                                />
                                <Checkbox
                                  id="reports_facility_email_activity"
                                  checked={staffState.permissions.reports_facility_email_activity}
                                  onChange={handleToggleChange}
                                  size="medium"
                                  label={t("Email Activity")}
                                  disabled={permissions?.user_id === staffState.id}
                                />
                                <Checkbox
                                  id="reports_facility_tee_sheet_no_show"
                                  checked={staffState.permissions.reports_facility_tee_sheet_no_show}
                                  onChange={handleToggleChange}
                                  size="medium"
                                  label={t("No Show")}
                                  disabled={permissions?.user_id === staffState.id}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card.Section>

                  <Card.Section>
                    <div className="flex items-start">
                      <div className="w-1/2">
                        <h1>{t("Tee Sheet")}</h1>
                      </div>

                      <div className="w-full">
                        <div>
                          <Checkbox
                            id="tee_sheet_credit_card_override"
                            checked={staffState.permissions.tee_sheet_credit_card_override}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Credit Card Override")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="tee_sheet_charge_no_show"
                            checked={staffState.permissions.tee_sheet_charge_no_show}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Charge No Show")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="booking_restrictions_edit"
                            checked={staffState.permissions.booking_restrictions_edit}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Edit Booking Restrictions")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                        </div>
                      </div>
                    </div>
                  </Card.Section>

                  <Card.Section>
                    <div className="flex items-start">
                      <div className="w-1/2">
                        <h1>{t("Orders")}</h1>
                      </div>

                      <div className="w-full">
                        <div>
                          <Checkbox
                            id="orders_edit_register"
                            checked={staffState.permissions.orders_edit_register}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Edit Order Register")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="orders_edit_user"
                            checked={staffState.permissions.orders_edit_user}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Edit Order User")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="orders_backdate"
                            checked={staffState.permissions.orders_backdate}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Orders Backdate")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="orders_refund_tip"
                            checked={staffState.permissions.orders_refund_tip}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Refund Tip")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="gift_cards_view"
                            checked={staffState.permissions.gift_cards_view}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("View Gift Card Details")}
                            disabled={permissions?.user_id === staffState.id}
                          />

                          {!!facilityPermissions?.gift_cards_import && (
                            <Checkbox
                              id="gift_cards_import"
                              checked={staffState.permissions.gift_cards_import}
                              onChange={handleToggleChange}
                              size="medium"
                              label={t("Import Gift Cards")}
                              disabled={permissions?.user_id === staffState.id}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </Card.Section>

                  <Card.Section>
                    <div className="flex items-start">
                      <div className="w-1/2">
                        <h1>{t("Table Service")}</h1>
                      </div>

                      <div className="w-full">
                        <div>
                          <Checkbox
                            id="table_service_table_override"
                            checked={staffState.permissions.table_service_table_override}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Table Override")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="orders_void"
                            checked={staffState.permissions.orders_void}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Void Orders")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                        </div>
                      </div>
                    </div>
                  </Card.Section>

                  <Card.Section>
                    <div className="flex items-start">
                      <div className="w-1/2">
                        <h1>{t("Reservations")}</h1>
                      </div>

                      <div className="w-full">
                        <div>
                          <Checkbox
                            id="reservations_credit_card_override"
                            checked={staffState.permissions.reservations_credit_card_override}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Credit Card Override")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="reservations_module_new"
                            checked={staffState.permissions.reservations_module_new}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Create Modules")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="reservations_module_edit"
                            checked={staffState.permissions.reservations_module_edit}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Edit Modules")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="reservations_location_new"
                            checked={staffState.permissions.reservations_location_new}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Create Locations")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="reservations_location_edit"
                            checked={staffState.permissions.reservations_location_edit}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Edit Locations")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                        </div>
                      </div>
                    </div>
                  </Card.Section>

                  <Card.Section>
                    <div className="flex items-start">
                      <div className="w-1/2">
                        <h1>{t("Cash Outs")}</h1>
                      </div>

                      <div className="w-full">
                        <div>
                          <Checkbox
                            id="cash_outs_create"
                            checked={staffState.permissions.cash_outs_create}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Create Cash Outs")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="cash_outs_edit"
                            checked={staffState.permissions.cash_outs_edit}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Edit Cash Outs")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="cash_outs_view_summary"
                            checked={staffState.permissions.cash_outs_view_summary}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("View Cash Out Summaries")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                        </div>
                      </div>
                    </div>
                  </Card.Section>

                  <Card.Section>
                    <div className="flex items-start">
                      <div className="w-1/2">
                        <h1>{t("Accounts")}</h1>
                      </div>

                      <div className="w-full">
                        <div>
                          <Checkbox
                            id="accounts_charge_all"
                            checked={staffState.permissions.accounts_charge_all}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Charge Any Account")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="accounts_post_adjustment"
                            checked={staffState.permissions.accounts_post_adjustment}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Adjust Account Balance")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="accounts_import"
                            checked={staffState.permissions.accounts_import}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Import Accounts")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="credit_books_make_adjustments"
                            checked={staffState.permissions.credit_books_make_adjustments}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Credit Book Adjustments")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                        </div>
                      </div>
                    </div>
                  </Card.Section>

                  <Card.Section>
                    <div className="flex items-start">
                      <div className="w-1/2">
                        <h1>{t("Memberships")}</h1>
                      </div>

                      <div className="w-full">
                        <div>
                          <Checkbox
                            id="memberships_apply_manual"
                            checked={staffState.permissions.memberships_apply_manual}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Manually Apply Memberships")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                        </div>
                      </div>
                    </div>
                  </Card.Section>

                  <Card.Section>
                    <div className="flex items-start">
                      <div className="w-1/2">
                        <h1>{t("Customers")}</h1>
                      </div>

                      <div className="w-full">
                        <div>
                          <Checkbox
                            id="user_edit_customer_type"
                            checked={staffState.permissions.user_edit_customer_type}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Edit Customer Type")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="customers_edit_note"
                            checked={staffState.permissions.customers_edit_note}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Edit Customer Notes")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="merge_customers"
                            checked={staffState.permissions.merge_customers}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Merge Customers")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="customers_ban_account"
                            checked={staffState?.permissions?.customers_ban_account}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Ban Customer Accounts")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                        </div>
                      </div>
                    </div>
                  </Card.Section>

                  <Card.Section>
                    <div className="flex items-start">
                      <div className="w-1/2">
                        <h1>{t("Facility")}</h1>
                      </div>

                      <div className="w-full">
                        <div>
                          <Checkbox
                            id="courses_create"
                            checked={staffState.permissions.courses_create}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Create Courses")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="divisions_create"
                            checked={staffState.permissions.divisions_create}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Create Divisions")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="payment_options_create"
                            checked={staffState.permissions.payment_options_create}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Create Payment Methods")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="payment_settings_edit"
                            checked={staffState.permissions.payment_settings_edit}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Edit Payment Settings")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                        </div>
                      </div>
                    </div>
                  </Card.Section>

                  <Card.Section>
                    <div className="flex items-start">
                      <div className="w-1/2">
                        <h1>{t("Booking Categories")}</h1>
                      </div>

                      <div className="w-full">
                        <div>
                          <Checkbox
                            id="booking_category_create"
                            checked={staffState.permissions?.booking_category_create}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Create Booking Category")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                        </div>
                      </div>
                    </div>
                  </Card.Section>

                  <Card.Section>
                    <div className="flex items-start">
                      <div className="w-1/2">
                        <h1>{t("Booking Notes")}</h1>
                      </div>

                      <div className="w-full">
                        <div>
                          <Checkbox
                            id="bookings_edit_note"
                            checked={staffState.permissions?.bookings_edit_note}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Edit Booking Notes")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                        </div>
                      </div>
                    </div>
                  </Card.Section>

                  <Card.Section>
                    <div className="flex items-start">
                      <div className="w-1/2">
                        <h1>{t("Bank Accounts")}</h1>
                      </div>

                      <div className="w-full">
                        <div>
                          <Checkbox
                            id="bank_accounts_create"
                            checked={staffState.permissions?.bank_accounts_create}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Create Bank Accounts")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                        </div>
                      </div>
                    </div>
                  </Card.Section>

                  <Card.Section>
                    <div className="flex items-start">
                      <div className="w-1/2">
                        <h1>{t("Tee On Payments")}</h1>
                      </div>

                      <div className="w-full">
                        <div>
                          <Checkbox
                            id="tee_on_payments_view_persons"
                            checked={staffState.permissions?.tee_on_payments_view_persons}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("View Persons")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                        </div>
                      </div>
                    </div>
                  </Card.Section>

                  <Card.Section>
                    <div className="flex items-start">
                      <div className="w-1/2">
                        <h1>{t("Departments")}</h1>
                      </div>

                      <div className="w-full">
                        <div>
                          <Checkbox
                            id="departments_create"
                            checked={staffState.permissions?.departments_create}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Create Departments")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                          <Checkbox
                            id="departments_edit"
                            checked={staffState.permissions?.departments_edit}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Edit Departments")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                        </div>
                      </div>
                    </div>
                  </Card.Section>

                  <Card.Section>
                    <div className="flex items-start">
                      <div className="w-1/2">
                        <h1>{t("Register")}</h1>
                      </div>

                      <div className="w-full">
                        <div>
                          <Checkbox
                            id="tabs_close"
                            checked={staffState.permissions?.tabs_close}
                            onChange={handleToggleChange}
                            size="medium"
                            label={t("Close tabs")}
                            disabled={permissions?.user_id === staffState.id}
                          />
                        </div>
                      </div>
                    </div>
                  </Card.Section>
                </Card>
              )}
          </Form>
        )}
      </div>

      <Popup
        open={resetPasswordPopup}
        type="info"
        title={t("Reset Account Password?")}
        description={t("An email will be sent to reset your account password")}
        onOk={handlePasswordReset}
        okText={t("Send")}
        cancelText={t("Cancel")}
        onCancel={() => setResetPasswordPopup(false)}
      />

      <Popup
        open={reactivateAccountPopup}
        type="info"
        title={t("Reactivate Staff Account?")}
        description={t("Are you sure you want to reactivate this staff account?")}
        onOk={handleReactivateAccount}
        okText={t("Activate")}
        cancelText={t("Cancel")}
        onCancel={() => setReactivateAccountPopup(false)}
      />
    </Page>
  );
}
